import {
  ChatStatus,
  fetchProjectChat,
  fetchProjectChatFile,
  useSDK,
  type LittlebirdSDK,
  type Message,
  type SharedChatDetail,
  type SharedContentFile,
  type Thread,
} from '@littlebird/sdk';
import { sharedDetailUnavailableMessage } from '@littlebird/ui-utils';
import { useQuery } from '@tanstack/react-query';

import { convertChatHistoryToMessages } from '@/features/chat/shared-chat/shared-chat.hooks';
import { projectChatQueryKey } from '@/features/team-chats/team-chats.query';
import { logError } from '@/lib/errorLogging';

export interface ProjectSharedChatDetail {
  title: string;
  authorUserId: number;
  authorName: string;
  messages: Message[];
  thread: Thread;
}

function hydrateFiles(
  sdk: LittlebirdSDK,
  projectId: string,
  chatId: string,
  files: SharedContentFile[]
) {
  for (const meta of files) {
    sdk.stores.files.addFile({
      id: meta.id,
      filename: meta.filename,
      contentType: meta.contentType,
      size: meta.size,
      width: meta.width ?? undefined,
      height: meta.height ?? undefined,
    });
    sdk.stores.files.registerFileUrl(meta.id, {
      url: meta.url,
      expiresIn: meta.expiresIn,
      refresh: async () => {
        const renewed = await fetchProjectChatFile(
          sdk,
          projectId,
          chatId,
          meta.id
        );
        return { url: renewed.url, expiresIn: renewed.expiresIn };
      },
    });
  }
}

async function toDetail(
  sdk: LittlebirdSDK,
  projectId: string,
  chat: SharedChatDetail
): Promise<ProjectSharedChatDetail> {
  const messages = await convertChatHistoryToMessages(
    chat.messages,
    chat.chatId
  );
  hydrateFiles(sdk, projectId, chat.chatId, chat.files);
  return {
    title: chat.title,
    authorUserId: chat.authorUserId,
    authorName: chat.authorName ?? chat.authorEmail ?? 'Someone',
    messages,
    thread: {
      id: chat.chatId,
      title: chat.title,
      starred: false,
      status: ChatStatus.Done,
    } as unknown as Thread,
  };
}

export function useProjectSharedChatDetail(projectId: string, chatId: string) {
  const sdk = useSDK();
  const query = useQuery({
    queryKey: projectChatQueryKey(projectId, chatId),
    // Loaded once per open, as before; a team chat's realtime nudge refreshes it
    gcTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    queryFn: async () => {
      try {
        const chat = await fetchProjectChat(sdk, projectId, chatId);
        return await toDetail(sdk, projectId, chat);
      } catch (error) {
        logError(error, 'Failed to load shared project chat', {
          context: 'useProjectSharedChatDetail',
          metadata: { projectId, chatId },
        });
        throw error;
      }
    },
  });
  return {
    detail: query.data ?? null,
    isLoading: query.isPending,
    error: query.isError ? sharedDetailUnavailableMessage('chat') : null,
  };
}
